export interface Section {
  id: string
  title: string
  executive: string
  details: string
  diagramId?: string
  interactive?: 'surfaces' | 'flow' | 'cache' | 'glossary'
}

/** Ordered documentation sections for the single-page site. */
export const sections: Section[] = [
  {
    id: 'at-a-glance',
    title: 'At a glance',
    executive:
      'Worldwide Golf serves product listings through a single integration path: the FastStore storefront talks to a VTEX middleware app (the bridge), which owns Unbxd credentials and returns catalog-ready products. Browsers never contact Unbxd directly. If Unbxd is unavailable or disabled, the storefront automatically falls back to native VTEX Intelligent Search — no redeploy required.',
    details: `<p>The integration spans two repositories:</p>
<ul>
  <li><strong>worldWideGolfQA-faststore</strong> — the customer-facing Next.js storefront (SSR, UI, GraphQL overrides).</li>
  <li><strong>worldwidegolfqa.faststore-unbxd-bridge</strong> — the VTEX IO app that calls Unbxd, hydrates VTEX catalog data, and exposes a FastStore-compatible GraphQL contract.</li>
</ul>
<p>Three customer-facing surfaces — Search (<code>/s</code>), category PLPs, and CMS landing pages — all share the same product gallery contract. One transport layer (<code>unbxdBridge.ts</code>) serves both server prefetch and client-side queries.</p>
<p><strong>Key property:</strong> Unbxd API keys live only in VTEX IO app settings. They are never exposed to browsers or the FastStore codebase.</p>`,
    diagramId: 'systemOverview',
  },
  {
    id: 'three-surfaces',
    title: 'The three surfaces',
    executive:
      'Shoppers encounter Unbxd-powered listings in three places: the search results page, category product listing pages, and merchandiser-authored landing pages. All three use the same gallery component and the same bridge, but differ in how content is authored and when results are prefetched on the server.',
    details: `<p>Despite different routes and CMS ownership, all surfaces converge on <code>SearchSsrPayload</code> → <code>CustomProductGallery</code>.</p>
<table class="data-table">
  <thead><tr><th>Surface</th><th>Route</th><th>Server strategy</th><th>Content owner</th></tr></thead>
  <tbody>
    <tr><td>Search</td><td><code>/s?q=…</code></td><td>getServerSideProps (per request)</td><td>Shopper query</td></tr>
    <tr><td>Category PLP</td><td><code>/:category-slug</code></td><td>getStaticProps + ISR</td><td>VTEX collection</td></tr>
    <tr><td>Landing page</td><td><code>/:landing-slug</code></td><td>getStaticProps + Unbxd prefetch</td><td>CMS merchandiser</td></tr>
  </tbody>
</table>
<p>Category PLPs resolve VTEX collections first, then pass category facets to the same search engine. Landing pages use the <code>UnbxdSearchResult</code> CMS section with a fixed URL (facet/sort changes do not alter the address bar).</p>`,
    interactive: 'surfaces',
  },
  {
    id: 'graphql-layers',
    title: 'GraphQL layers',
    executive:
      'Search traffic passes through two GraphQL layers before reaching Unbxd. The FastStore storefront exposes a familiar <code>Query.search</code> interface to the UI. Behind it, a dedicated bridge resolver translates requests into Unbxd API calls, hydrates full product data from the VTEX catalog, and returns a standardized result object.',
    details: `<p><strong>Layer 1 — Storefront GraphQL</strong> (<code>/api/graphql</code>)</p>
<ul>
  <li>Overrides <code>Query.search</code> and <code>Query.redirect</code> when <code>ENABLE_UNBXD_SEARCH=true</code>.</li>
  <li>Wraps bridge responses as <code>{ __engine: 'unbxd', payload }</code>; field resolvers map to native FastStore types.</li>
  <li>On failure, transparently falls back to VTEX Intelligent Search.</li>
</ul>
<p><strong>Layer 2 — VTEX tenant-graphql aggregator</strong></p>
<ul>
  <li>Endpoint: <code>/_v/private/graphql/v1</code> (configured as <code>UNBXD_BRIDGE_URL</code>).</li>
  <li>Plain <code>/_v/graphql</code> returns HTTP 405 — always use the tenant aggregator.</li>
</ul>
<p><strong>Layer 3 — Bridge resolvers</strong></p>
<ul>
  <li><code>faststoreSearch</code> — main SRP/PLP search (validate → Unbxd → hydrate → map → cache policy).</li>
  <li><code>faststoreRedirect</code> — Unbxd redirect probe with <code>rows=0</code> (no product hydration).</li>
</ul>
<p><strong>Two storefront entry points</strong> hit the same bridge transport:</p>
<ol>
  <li><strong>Server prefetch</strong> — GSSP on <code>/s</code>, GSP on PLP/landing (direct POST via <code>unbxdBridge.ts</code>).</li>
  <li><strong>Client queries</strong> — SWR-driven <code>/api/graphql</code> for filters, sort, pagination, and header typeahead.</li>
</ol>`,
    diagramId: 'graphqlLayers',
  },
  {
    id: 'search-flow',
    title: 'Search flow',
    executive:
      'When a shopper searches, the server prefetches results before the page is sent to the browser. The first page of products and filters appears in the initial HTML — visible even with JavaScript disabled. When the shopper changes filters or sort order, the browser fetches updated results without a full page reload.',
    details: `<p><strong>Server-side (first paint)</strong></p>
<ol>
  <li>Shopper requests <code>/s?q=mens+shoes</code>.</li>
  <li>GSSP checks <code>ENABLE_UNBXD_SEARCH</code> and parses query parameters.</li>
  <li>Storefront POSTs to the bridge; bridge calls Unbxd, hydrates VTEX catalog rows, returns <code>FsSearchResult</code>.</li>
  <li>Response is mapped to <code>SearchSsrPayload</code> and embedded in page props.</li>
  <li>HTML ships with real product cards (<code>data-fs-search-ssr-products</code>) and filter sidebar markup.</li>
  <li>Cache-Control: <code>private, no-store</code> when SSR data is present.</li>
</ol>
<p><strong>Client-side (interactions)</strong></p>
<ol>
  <li><code>useHydrateSearchGallery</code> seeds SWR cache from SSR payload before hydration.</li>
  <li><code>searchSsrMatchesQuery</code> verifies query-key parity — mismatch forces client refetch (skeleton flash).</li>
  <li>Filter/sort/pagination changes trigger SWR fetch via the same <code>Query.search</code> override.</li>
  <li><code>useSearchGalleryPage</code> keeps previous results visible during refetch (no blank grid).</li>
</ol>
<p><strong>Resilience:</strong> circuit breaker (5 failures / 30s cooldown), bounded retries, automatic VTEX IS fallback.</p>`,
    diagramId: 'searchFlow',
    interactive: 'flow',
  },
  {
    id: 'plp-flow',
    title: 'PLP flow',
    executive:
      'Category pages (e.g. Golf Clubs → Drivers) use VTEX collections to define which products belong to the category. The same Unbxd search engine powers the listing, with category facets derived from collection metadata. Pages are generated statically and revalidated on demand for performance at scale.',
    details: `<p><strong>Build-time / on-demand generation</strong></p>
<ol>
  <li>Request hits catch-all route <code>[...slug].tsx</code>.</li>
  <li>If no CMS landing exists, VTEX <code>ServerCollectionPageQuery</code> resolves the category.</li>
  <li>Category facets (e.g. <code>category-1</code>, <code>category-2</code>) are built from collection metadata.</li>
  <li><code>fetchServerManyProducts</code> calls storefront GraphQL (same <code>Query.search</code> override).</li>
  <li>Bridge routes empty-term + category facets to Unbxd <code>/category</code> endpoint.</li>
  <li><code>ProductListing</code> renders with <code>forceInteractive=true</code> (no 823px JS placeholder).</li>
</ol>
<p><strong>PLP vs Search differences</strong></p>
<ul>
  <li>PLP always sets <code>trustSsrFirstPage: true</code> — GSP returns a complete first page.</li>
  <li>ISR: category PLPs use <code>fallback: 'blocking'</code>; landing pages use <code>revalidate: 60</code>.</li>
  <li>Content is collection-driven, not shopper-entered search terms.</li>
</ul>`,
    diagramId: 'plpFlow',
  },
  {
    id: 'landing-pages',
    title: 'Landing pages',
    executive:
      'Merchandisers create promotional or brand-focused pages in VTEX CMS using the UnbxdSearchResult section. These pages run fixed Unbxd queries (e.g. "TaylorMade Qi4d products under $500") without tying to a VTEX category. Results are prefetched at build time and revalidated every 60 seconds.',
    details: `<p><strong>Authoring flow</strong></p>
<ol>
  <li>Merchandiser creates a landing page in VTEX Headless CMS.</li>
  <li>Adds an <code>UnbxdSearchResult</code> section with query, facets, sort, and price range fields.</li>
  <li>On GSP, <code>findUnbxdSection</code> locates the section and calls <code>prefetchUnbxdLanding</code>.</li>
  <li>Payload is stored in <code>globalSettings.initialSearchGallery</code>.</li>
  <li>Section mounts a nested <code>PageProvider</code> so filter sidebar renders in SSR HTML.</li>
</ol>
<p><strong>Merchandising contract</strong></p>
<ul>
  <li>URL stays fixed when shoppers change facets or sort (no URL mutation).</li>
  <li>Unbxd redirect rules apply at prefetch time (307 to destination).</li>
  <li>Bridge failure degrades to client-side SWR fetch (skeleton until resolved).</li>
</ul>`,
    diagramId: 'landingFlow',
  },
  {
    id: 'cache-strategy',
    title: 'Cache strategy',
    executive:
      'Caching is deliberately conservative. Personalized sessions (logged-in shoppers, regional pricing, non-default sales channels) always receive fresh results. Anonymous visitors on default settings may see briefly cached responses — up to 60 seconds at the API layer and 30 seconds inside the bridge worker.',
    details: `<p>The bridge owns cache semantics; the storefront mirrors them onto HTTP <code>Cache-Control</code> headers.</p>
<p><strong>Bridge DTO policy</strong> (<code>chooseCachePolicy</code>):</p>
<ul>
  <li><code>NO_STORE</code> — request has cookies, QA override header, or non-default channel/region.</li>
  <li><code>PUBLIC, 60s</code> — anonymous visitor, default channel and region.</li>
</ul>
<p><strong>Upstream worker memory</strong> — 30s TTL for identical Unbxd + catalog calls when policy is PUBLIC.</p>
<p><strong>Search page HTTP</strong> — <code>private, no-store</code> when SSR data is present; otherwise <code>public, s-maxage=300</code>.</p>
<p><strong>Browser SWR</strong> — in-memory cache seeded from SSR; header typeahead warms cache before navigation to <code>/s</code>.</p>
<p><strong>Business rule of thumb:</strong> if the result could differ per shopper, we do not cache it at the CDN.</p>`,
    diagramId: 'cacheLayers',
    interactive: 'cache',
  },
  {
    id: 'resilience',
    title: 'Resilience & rollback',
    executive:
      'The integration is designed for safe rollback. Disabling Unbxd via environment variable immediately routes all search traffic back to VTEX Intelligent Search. Bridge failures trigger automatic fallback — shoppers see results, not error pages. A circuit breaker prevents hammering a degraded bridge.',
    details: `<p><strong>Emergency rollback</strong></p>
<ol>
  <li>Set <code>ENABLE_UNBXD_SEARCH=false</code> in deployment environment.</li>
  <li>Restart the storefront (no rebuild required).</li>
  <li>All <code>Query.search</code> calls route to native VTEX Intelligent Search.</li>
</ol>
<p><strong>Automatic fallback triggers</strong></p>
<ul>
  <li>Bridge HTTP timeout or transport error.</li>
  <li>Circuit breaker open (5 failures in 60s → 30s cooldown).</li>
  <li>Bridge resolver throws (Unbxd unavailable, catalog unavailable, invalid response).</li>
</ul>
<p><strong>QA override</strong> — when <code>ENABLE_UNBXD_QA_OVERRIDE=true</code>, header <code>x-fs-search-engine: unbxd|is</code> forces engine per request (cache disabled).</p>
<p><strong>Validation errors</strong> — bridge returns empty <code>NO_STORE</code> result (no throw); storefront may still fall back depending on context.</p>`,
    diagramId: 'resilience',
  },
  {
    id: 'glossary',
    title: 'Glossary & repositories',
    executive:
      'Quick reference for terms used throughout this guide, plus links to the source repositories for engineers who need implementation detail.',
    details: `<p>Use the glossary below for definitions. Engineers should consult the linked repositories and the full integration doc for runbooks and verification commands.</p>
<p><strong>Repositories</strong></p>
<ul>
  <li><a href="https://github.com/worldwidegolf/worldWideGolfQA-faststore" target="_blank" rel="noopener">worldWideGolfQA-faststore</a> — storefront, SSR, GraphQL overrides</li>
  <li><a href="https://github.com/worldwidegolf/worldwidegolfqa.faststore-unbxd-bridge" target="_blank" rel="noopener">worldwidegolfqa.faststore-unbxd-bridge</a> — VTEX IO bridge app</li>
</ul>
<p><strong>Engineer documentation</strong></p>
<ul>
  <li>Storefront: <code>docs/unbxd-integration.md</code></li>
  <li>Bridge: <code>AGENTS.md</code>, <code>docs/unbxd-mapping.md</code></li>
</ul>`,
    interactive: 'glossary',
  },
]
